import { PackagingCarbonIntensityCategoryEnum } from '@shared/api/types';
import { cn } from '@shared/lib/utils';

export const PACKAGING_RATING_CONFIG: Record<
  PackagingCarbonIntensityCategoryEnum,
  { label: string; color: string; threshold: string }
> = {
  Low: { label: 'Low', color: 'var(--rating-a)', threshold: '<1.25' },
  Medium: {
    label: 'Medium',
    color: 'var(--rating-c)',
    threshold: '1.25 - 2.5',
  },
  High: { label: 'High', color: 'var(--rating-e)', threshold: '>2.5' },
};

interface PackagingRatingProps {
  /** Category from the API; `null` renders nothing (item not yet rated). */
  category: PackagingCarbonIntensityCategoryEnum | null | undefined;
  /** When true, also render the label + threshold beside the swatch. */
  showThreshold?: boolean;
  className?: string;
}

/**
 * Visual indicator for packaging carbon intensity (Low / Medium / High).
 * The colour swatch is the standard form used in tables; pass `showThreshold`
 * for the legend variant used in preview sheets and chart tooltips.
 */
export function PackagingRating({
  category,
  showThreshold,
  className,
}: PackagingRatingProps) {
  if (!category) return null;
  const { label, color, threshold } = PACKAGING_RATING_CONFIG[category];

  if (!showThreshold) {
    return (
      <div
        className={cn('size-4 rounded-sm', className)}
        style={{ backgroundColor: color }}
        aria-label={`Rating: ${label}`}
      />
    );
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className="size-4 shrink-0 rounded-sm"
        style={{ backgroundColor: color }}
      />
      <span>
        {label}
        <span className="ml-1 text-gray-500">{threshold}</span>
      </span>
    </div>
  );
}
